import { createFileRoute, redirect } from "@tanstack/react-router";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { AccountDeletionGate } from "@/features/account/components/AccountDeletionGate";
import { MfaSetupGate } from "@/features/account/components/MfaSetupGate";
import { checkAuthenticated } from "@/features/auth/api/auth.api";
import { ApiError, clearAccessKey, getAccessKey } from "@/lib/api/client";
import { ErrorFallback } from "@/components/ui/ErrorFallback";

const AUTH_PROBE_ATTEMPTS = 3;

/**
 * Authenticated layout route — wraps all authenticated pages
 * in the DashboardShell (sidebar + header + content).
 *
 * beforeLoad:
 * 1. Checks if an access key exists in localStorage.
 * 2. Validates the token against `GET /v1/authenticated`.
 * 3. A 401 for this key (or one the interceptor already cleared) redirects
 *    to /login. A transient failure is rethrown so ErrorFallback can retry
 *    in place. A 401 after another tab replaced the key re-probes the new one.
 *
 * This runs once per navigation, not on every child route change
 * (TanStack Router caches layout beforeLoad results).
 */
export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
  errorComponent: ErrorFallback,
  beforeLoad: async () => {
    for (let attempt = 0; attempt < AUTH_PROBE_ATTEMPTS; attempt++) {
      const accessKey = getAccessKey();
      if (!accessKey) {
        throw redirect({ to: "/login" });
      }

      try {
        await checkAuthenticated();
        return;
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 401) {
          throw err;
        }

        const current = getAccessKey();
        if (current === accessKey || current === null) {
          if (current === accessKey) {
            clearAccessKey();
          }
          throw redirect({ to: "/login" });
        }
      }
    }

    throw new Error();
  },
});

function AuthenticatedLayout() {
  return (
    <AccountDeletionGate>
      <MfaSetupGate>
        <DashboardShell />
      </MfaSetupGate>
    </AccountDeletionGate>
  );
}
